import type { ColorsType } from "Infrastructure/Themes/Themes";

/**
 * The single source of truth for every colour in the app.
 *
 * Nothing outside this file should contain a hex literal — if you need a shade
 * that is not here, add a named token rather than inlining it. Consume it
 * either through the MUI theme (`theme.palette.*`, `theme.colors.*`) or, for
 * module-level constants where no theme is in scope, by importing `colors()`.
 *
 * Note this is the product palette, which is deliberately a little warmer than
 * the brand exports in `assets/brand/`. The logo, app icons and email artwork
 * keep the designer's violet (#8B5CF6); the UI does not have to match it.
 */
export const colors = (): ColorsType => ({
  PrimaryMain: "#AA68FF",
  PrimaryLight: "#BB86FF",
  PrimaryDark: "#7648B2",
  PrimaryContrastText: "#ffffff",
  SecondaryMain: "#5f5f5f",
  SecondaryLight: "#7F7F7F",
  SecondaryDark: "#424242",
  SuccessMain: "#4caf50",
  SuccessLight: "#81C784",
  SuccessDark: "#388E3C",
  InfoMain: "#5B8EFB",
  InfoLight: "#7BA4FB",
  InfoDark: "#3F63AF",
  WarningMain: "#FF9800",
  WarningLight: "#FFAC33",
  WarningDark: "#B26A00",
  /** Muted amber for "waiting on something external" states. */
  WarningPale: "#ffc670",
  ErrorMain: "#FB5B5B",
  ErrorLight: "#FB7B7B",
  ErrorDark: "#AF3F3F",
  TextPrimary: "#ffffff",
  TextSecondary: "rgba(255, 255, 255, 0.7)",
  TextDisabled: "rgba(255, 255, 255, 0.5)",
  TextTertiary: "#686F76",
  /** Inactive sidebar/menu labels — brighter than TextSecondary. */
  TextLight: "#D2D2D2",
  BackgroundDefault: "#212529",
  BackgroundInput: "#13181D",
  BackgroundTile: "#2C3033",
  /** Hover state for tile surfaces (sortable table headers). */
  BackgroundTileHover: "#363A3D",
  /** Backdrop behind modal media, e.g. the profile picture cropper. */
  BackgroundScrim: "#111111",
  Border: "#404040",
  /** Structural dividers inside the sidebar. */
  BorderSubtle: "#2E3337",
  /** Row separators in data tables. Within ~2% lightness of BorderSubtle — the
   * two are kept apart only to preserve the existing look; merge if you prefer. */
  BorderTable: "#32373C",
  ReactionAccent: "#E91E63",
  Black: "#000000",
  White: "#ffffff",
  Gray: "#F1F5F9",
});

/**
 * Categorical series palette for Reporting charts. Ordered so the first few
 * entries stay distinguishable when a chart only plots two or three series.
 */
export const chartSeriesColors = [
  colors().PrimaryMain,
  colors().InfoMain,
  colors().SuccessMain,
  colors().WarningMain,
  colors().ErrorMain,
  colors().PrimaryLight,
  colors().SuccessLight,
  colors().WarningLight,
  colors().InfoLight,
  colors().SecondaryLight,
] as const;

/** Per-speaker highlight colours in the script editor, cycled by speaker index. */
export const speakerColors = [
  colors().PrimaryMain,
  colors().WarningMain,
  "#4CAF50",
  "#2196F3",
  colors().ReactionAccent,
  "#00BCD4",
  "#FF5722",
  "#9C27B0",
  "#26A69A",
  "#795548",
] as const;

/** Avatar background rotation in DetailSection, cycled by name hash. */
export const avatarColors = [
  "#5C6BC0",
  "#E65100",
  "#2E7D32",
  "#6A1B9A",
  "#00838F",
  "#AD1457",
] as const;
